import React from "react";
import "./Products.scss";
import {
  Typography,
  Input,
  Select,
  Button,
  Badge,
  Pagination,
  Spin,
  Layout,
} from "antd";
import {
  CustomEmpty,
  ProductCard,
  ProductDrawer,
  PromoCodesSlider,
  ViewSwitch,
} from "../../components";
import {
  SearchOutlined,
  FunnelPlotOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { ProductFilters, SelectedFilters } from "./components";
import useProducts from "../../hooks/useProducts";
import classNames from "classnames";
import Utils from "../../utils";

const { Content } = Layout;

export default function Products() {
  const {
    contextHolder,
    token,
    isAuthenticated,
    isMobile,
    meta,
    loading,
    searchParams,
    setIsFilterOpened,
    isFilterOpened,
    onSearchInpChange,
    searchVal,
    onOrderChange,
    onOrderClear,
    categories,
    catgeoriesLoading,
    onCategoryChange,
    brands,
    brandsLoading,
    onBrandChange,
    onGeneticsChange,
    priceRange,
    onPriceRangeChange,
    onCheckboxChange,
    onClearFilters,
    onPriceRangeClear,
    maxPrice,
    productContRef,
    products,
    navigate,
    openDrawer,
    handlePagination,
    width,
    isDrawerOpened,
    setIsDrawerOpened,
    setSelectedProduct,
    selectedProduct,
    view,
    onViewChange,
  } = useProducts(".products-cont");
  const isList = view === "list";

  const selectedCategories = searchParams?.get("category")?.split(",") || [];
  const selectedBrands = searchParams?.get("brand_ids")?.split(",") || [];
  const selectedGenetics = searchParams?.get("genetics")?.split(",") || [];
  const isOnsale = Boolean(searchParams?.get("sale")) || false;
  const sortValue =
    searchParams?.get("order_by") === "price"
      ? "ascending"
      : searchParams?.get("order_by") === "-price"
      ? "descending"
      : undefined;

  // The full range — 0 to the shop's highest price — is the slider's resting
  // state, not a choice the customer made, so it is never shown as a filter
  // even when those two values are in the URL.
  const priceFrom = Number(searchParams?.get("price_gte")) || 0;
  const priceTo = Number(searchParams?.get("price_lte")) || maxPrice || 0;
  const isPriceFiltered =
    Boolean(maxPrice) && (priceFrom > 0 || priceTo < maxPrice);

  // One removable entry per filter in the URL. Removing goes through the same
  // handlers the filter controls call, so the URL and the brand list behave
  // exactly as they do on a manual change.
  const selectedFilters = [
    ...selectedCategories?.map((name) => ({
      key: `category-${name}`,
      label: name,
      onRemove: () =>
        onCategoryChange(
          undefined,
          categories
            ?.filter(
              (category) =>
                category?.name !== name &&
                selectedCategories?.includes(category?.name)
            )
            ?.map((category) => ({
              label: category?.name,
              value: category?.id,
            }))
        ),
    })),
    ...selectedBrands?.map((id) => ({
      key: `brand-${id}`,
      label: brands?.find((brand) => `${brand?.id}` === id)?.label || id,
      onRemove: () =>
        onBrandChange(selectedBrands?.filter((brandId) => brandId !== id)),
    })),
    ...selectedGenetics?.map((genetic) => ({
      key: `genetics-${genetic}`,
      label: `${genetic?.charAt(0)?.toUpperCase()}${genetic?.slice(1)}`,
      onRemove: () =>
        onGeneticsChange(selectedGenetics?.filter((item) => item !== genetic)),
    })),
    ...(isPriceFiltered
      ? [
          {
            key: "price",
            label: `$${priceFrom} – $${priceTo}`,
            onRemove: onPriceRangeClear,
          },
        ]
      : []),
    ...(isOnsale
      ? [
          {
            key: "sale",
            label: "On sale",
            onRemove: () => onCheckboxChange({ target: { checked: false } }),
          },
        ]
      : []),
  ];

  // On mobile the sort lives inside "Filter & sort", so there it counts as one
  // more thing set, and "Clear all" resets it together with the filters.
  const mobileActiveCount = selectedFilters?.length + (sortValue ? 1 : 0);
  // The mobile tags: the sort first, then the filters.
  const mobileTags = [
    ...(sortValue
      ? [
          {
            key: "sort",
            label:
              sortValue === "ascending"
                ? "Price: Low to High"
                : "Price: High to Low",
            onRemove: onOrderClear,
          },
        ]
      : []),
    ...selectedFilters,
  ];

  const countTexts = (
    <>
      <Typography.Text
        className="products-cont__globalFilters__productsCountWrapper__text"
        style={{
          color: token?.Typography?.mainColorText,
        }}>
        All Products
      </Typography.Text>
      <div className="products-cont__globalFilters__productsCountWrapper__textsWrapper">
        <Typography.Text
          className="products-cont__globalFilters__productsCountWrapper__text"
          style={{
            color: token?.Typography?.colorPrimaryText,
          }}>
          {meta?.count ? meta?.count : 0}
        </Typography.Text>
        <Typography.Text
          className="products-cont__globalFilters__productsCountWrapper__text"
          style={{
            color: token?.Typography?.mainColorText,
          }}>
          results
        </Typography.Text>
      </div>
    </>
  );

  const searchInput = (
    <Input
      className="products-cont__globalFilters__filtersWrapper__searchInp"
      size="large"
      placeholder="Brands, products, retailers and more"
      prefix={<SearchOutlined />}
      allowClear
      onChange={onSearchInpChange}
      value={searchVal}
    />
  );

  const viewSwitch = (
    <ViewSwitch
      view={view}
      onChange={onViewChange}
      size={isMobile ? "middle" : "large"}
    />
  );

  return (
    <>
      {contextHolder}
      <Content
        className="products-cont"
        style={{
          backgroundColor: token?.Layout?.contentBg,
        }}>
        <PromoCodesSlider isAuthenticated={isAuthenticated} token={token} />
        {isMobile ? (
          <>
            <div
              className="products-cont__mobileBar"
              style={{
                backgroundColor: token?.Layout?.contentBg,
              }}>
              {searchInput}
              <div className="products-cont__mobileBar__chips">
                <Button
                  size="large"
                  icon={<FunnelPlotOutlined />}
                  onClick={() => {
                    setIsFilterOpened(true);
                  }}>
                  Filter &amp; sort
                  <Badge
                    count={mobileActiveCount}
                    size="small"
                    color={token?.colorPrimary}
                  />
                </Button>
                <SelectedFilters
                  token={token}
                  items={mobileTags}
                  disabled={loading || catgeoriesLoading}
                />
              </div>
            </div>
            <div className="products-cont__resultsRow">
              <div className="products-cont__globalFilters__productsCountWrapper__textsWrapper">
                {countTexts}
              </div>
              <div className="products-cont__resultsRow__actions">
                {mobileActiveCount ? (
                  <Button
                    type="link"
                    size="small"
                    disabled={loading}
                    onClick={() => onClearFilters({ withOrder: true })}>
                    Clear all
                  </Button>
                ) : null}
                {viewSwitch}
              </div>
            </div>
          </>
        ) : (
          <div
            className="products-cont__globalFilters"
            style={{
              backgroundColor: token?.Layout?.contentBg,
            }}>
            <div className="products-cont__globalFilters__productsCountWrapper">
              {countTexts}
            </div>
            <div className="products-cont__globalFilters__filtersWrapper">
              {searchInput}
              <div className="products-cont__globalFilters__filtersWrapper__actions">
                <Select
                  className="products-cont__globalFilters__filtersWrapper__select"
                  aria-label="Sort by price"
                  placeholder="Sort by price"
                  prefix={<SwapOutlined rotate={90} />}
                  size="large"
                  options={[
                    { label: "Price: Low to High", value: "ascending" },
                    { label: "Price: High to Low", value: "descending" },
                  ]}
                  allowClear
                  onChange={onOrderChange}
                  disabled={loading}
                  onClear={onOrderClear}
                  value={sortValue}
                />
                {viewSwitch}
              </div>
            </div>
          </div>
        )}
        <div className="products-cont__products">
          <ProductFilters
            token={token}
            isMobile={isMobile}
            isFilterOpened={isFilterOpened}
            onClose={() => {
              setIsFilterOpened(false);
            }}
            loading={loading}
            categories={categories}
            catgeoriesLoading={catgeoriesLoading}
            selectedCategories={selectedCategories}
            onCategoryChange={onCategoryChange}
            filterOption={Utils?.filterOption}
            brands={brands}
            brandsLoading={brandsLoading}
            selectedBrands={selectedBrands}
            onBrandChange={onBrandChange}
            selectedGenetics={selectedGenetics}
            onGeneticsChange={onGeneticsChange}
            priceRange={priceRange}
            onPriceRangeChange={onPriceRangeChange}
            isOnsale={isOnsale}
            onCheckboxChange={onCheckboxChange}
            maxPrice={maxPrice}
            sortValue={sortValue}
            onOrderChange={onOrderChange}
            onOrderClear={onOrderClear}
            activeCount={isMobile ? mobileActiveCount : selectedFilters?.length}
            resultsCount={meta?.count}
            onClearAll={() => onClearFilters()}
            onReset={() => onClearFilters({ withOrder: true })}
          />
          <Spin
            size="large"
            spinning={loading}
            classNames={{
              root: classNames("products-cont__products__spin", {
                "products-cont__spinWrapper": loading || !products?.length,
                "products-cont__spinCentered": !products?.length,
              }),
            }}>
            <div
              ref={productContRef}
              style={{
                gridTemplateColumns:
                  !loading && !products?.length
                    ? `repeat(auto-fit, minmax(${"100%"},1fr))`
                    : isList
                    ? "minmax(0, 1fr)"
                    : `repeat(auto-fit, minmax(${
                        isMobile ? "168px" : "179px"
                      }, ${
                        isMobile
                          ? 350
                          : 250 / productContRef?.current?.offsetWidth
                      }fr))`,
              }}
              className={"products-cont__products__cartsCont"}>
              {!products?.length && !loading ? (
                <CustomEmpty
                  circleColor={
                    token?.icons?.emptyCircleIconColor
                      ? token?.icons?.emptyCircleIconColor
                      : ""
                  }
                  boxBorderColor={
                    token?.icons?.emptyIconBoxBorderColor
                      ? token?.icons?.emptyIconBoxBorderColor
                      : ""
                  }
                />
              ) : (
                products?.map((product, index) => (
                  <ProductCard
                    product={product}
                    key={product?.id ?? `product-${product?.name}-${index}`}
                    navigate={() => {
                      navigate(`product/${product?.id}`, { state: product });
                      Utils?.scrollIntoViewByClassName(".appHeader-cont");
                    }}
                    haveDrawer={true}
                    openDrawer={openDrawer}
                    layout={isList ? "list" : "grid"}
                  />
                ))
              )}
            </div>
          </Spin>
        </div>
        <Pagination
          className="products-cont__pagination"
          disabled={loading}
          pageSize={searchParams?.get("per_page") || 25}
          current={searchParams?.get("page") || 1}
          defaultCurrent={searchParams?.get("page") || 1}
          total={meta?.count ? meta?.count : ""}
          responsive={true}
          onChange={handlePagination}
          showQuickJumper={width < 584 && width >= 577 ? false : true}
          showSizeChanger={false}
        />
        <ProductDrawer
          isDrawerOpened={isDrawerOpened}
          close={() => {
            setIsDrawerOpened(false);
            setTimeout(() => {
              setSelectedProduct(null);
            }, 300);
          }}
          product={selectedProduct}
        />
      </Content>
    </>
  );
}
